import { todayInArgentina } from '@catering/ui';
import { z, type ZodSchema } from 'zod';

export type SettingInputType = 'text' | 'number';

/**
 * Mirrors `apps/api/src/modules/settings/domain/setting-catalog.ts`: same two
 * keys, same validation rules. A change to a rule happens in one place per
 * side — this file, plus its backend counterpart — never inside the form.
 */
export interface ClientSettingDefinition {
  key: string;
  category: string;
  label: string;
  helpText: string;
  inputType: SettingInputType;
  schema: ZodSchema;
  invalidMessage: string;
  /** Specific Spanish message per failure; falls back to `invalidMessage`. */
  explain?: (raw: string) => string | undefined;
  /** Shown next to the field while it is editable (`system-settings/spec.md`). */
  warning?: string;
  /** Preview of what the typed value produces; `undefined` while it is invalid. */
  preview?: (raw: string) => string | undefined;
}

const CUTOFF_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

const RECEIPT_MAX_LENGTH = 40;
const RECEIPT_TOKENS = ['{SEQUENCE}', '{YYYYMM}', '{YYYY}', '{DD}'] as const;
const RECEIPT_LITERAL = /^[A-Za-z0-9._-]*$/;

/**
 * Mirrors `validateReceiptNumberFormat` in the API catalog, with Spanish
 * messages. Returns the first broken rule, or `undefined` when valid.
 */
function explainReceiptFormat(raw: string): string | undefined {
  if (raw.length > RECEIPT_MAX_LENGTH) {
    return `El formato no puede superar los ${RECEIPT_MAX_LENGTH} caracteres.`;
  }

  const found: string[] = raw.match(/\{[^}]*\}/g) ?? [];

  for (const token of found) {
    if (!(RECEIPT_TOKENS as readonly string[]).includes(token)) {
      return `El token ${token} no es válido. Usá {SEQUENCE}, {YYYYMM}, {YYYY} o {DD}.`;
    }
  }

  for (const token of RECEIPT_TOKENS) {
    if (found.filter((candidate) => candidate === token).length > 1) {
      return `El token ${token} puede aparecer una sola vez.`;
    }
  }

  if (!found.includes('{SEQUENCE}')) {
    return 'El formato debe incluir {SEQUENCE}.';
  }

  const literal = raw.replace(/\{[^}]*\}/g, '');

  if (!RECEIPT_LITERAL.test(literal)) {
    return 'Solo se permiten letras, números y los caracteres . - _ fuera de los tokens.';
  }

  return undefined;
}

/** Same substitution as the API's `renderNoteNumber`; `undefined` if the format is invalid. */
export function previewReceiptNumber(
  format: string,
  sequence: number,
  issuedAt: Date,
): string | undefined {
  if (explainReceiptFormat(format) !== undefined) {
    return undefined;
  }

  const [yyyy = '', mm = '', dd = ''] = todayInArgentina(issuedAt).split('-');

  return format
    .replace('{YYYYMM}', `${yyyy}${mm}`)
    .replace('{YYYY}', yyyy)
    .replace('{DD}', dd)
    .replace('{SEQUENCE}', String(sequence).padStart(5, '0'));
}

export const SETTING_CATALOG: readonly ClientSettingDefinition[] = [
  {
    key: 'order.default_cutoff_time',
    category: 'order',
    label: 'Hora de cierre de pedidos',
    helpText:
      'Hora del día (formato 24 horas) a partir de la cual el sistema deja de aceptar pedidos nuevos para la fecha de servicio correspondiente.',
    inputType: 'text',
    schema: z.string().regex(CUTOFF_TIME_PATTERN),
    invalidMessage: 'Ingresá una hora válida en formato HH:mm (00:00 a 23:59).',
  },
  {
    key: 'order.cutoff_days_before',
    category: 'order',
    label: 'Días de anticipación del cierre',
    helpText:
      'Cantidad de días antes de la fecha de servicio en que se aplica el cierre de pedidos. Por ejemplo, con 1 día de anticipación, los pedidos del jueves cierran el miércoles a la hora configurada arriba.',
    inputType: 'number',
    schema: z.number().int().min(0),
    invalidMessage: 'Ingresá un número entero mayor o igual a 0.',
  },
  {
    key: 'payment.receipt_number_format',
    category: 'payment',
    label: 'Formato del número de recibo',
    helpText:
      'Armá el número con estas piezas: {SEQUENCE} es el número correlativo y es obligatorio; {YYYYMM} es año y mes, {YYYY} el año y {DD} el día (opcionales, cada uno una sola vez). Ejemplo: REC-{YYYYMM}-{SEQUENCE} da REC-202610-00001. Hasta 40 caracteres; además de esas piezas, solo podés usar letras, números y . - _',
    inputType: 'text',
    schema: z.string().refine((value) => explainReceiptFormat(value) === undefined),
    invalidMessage: 'El formato del recibo no es válido. Revisá los tokens y los caracteres.',
    explain: explainReceiptFormat,
    warning:
      'Los recibos ya emitidos conservan su número y la numeración continúa: cambiar el formato no la reinicia.',
    preview: (raw) => previewReceiptNumber(raw, 1, new Date()),
  },
] as const;

export function findSettingDefinition(key: string): ClientSettingDefinition | undefined {
  return SETTING_CATALOG.find((definition) => definition.key === key);
}

/** The value to send in `PATCH /settings/:key`: a string as typed, or a parsed number. */
export function parseSettingValue(definition: ClientSettingDefinition, raw: string): unknown {
  return definition.inputType === 'number' ? Number(raw) : raw;
}

/**
 * Client-side mirror of the server's zod validation, so the form can show a
 * Spanish message before ever calling `PATCH /settings/:key`.
 */
export function validateSettingValue(
  definition: ClientSettingDefinition,
  raw: string,
): string | undefined {
  if (definition.inputType === 'number' && raw.trim() === '') {
    return definition.invalidMessage;
  }

  const parsed = definition.schema.safeParse(parseSettingValue(definition, raw));

  return parsed.success
    ? undefined
    : (definition.explain?.(raw) ?? definition.invalidMessage);
}
